/**
 * me/progress.tsx — Full-fidelity progress surface.
 *
 * Lives at /me/progress. Renders the same widgets as /me but with a
 * wider activity feed (12 entries) and a "Lihat semua aktivitas" link
 * placeholder for when the activity archive lands in Fase 6+.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { ProgressHeader } from '../../components/student/ProgressHeader.js';
import { GameProgressCard } from '../../components/student/GameProgressCard.js';
import { WorksheetProgressCard } from '../../components/student/WorksheetProgressCard.js';
import { RecentActivityFeed } from '../../components/student/RecentActivityFeed.js';
import {
  freshSessionQuery,
  myProgressQuery,
  type SessionPrincipal,
  type ProgressSummary,
} from '../../lib/api/queries.js';

export const Route = createFileRoute('/me/progress')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'admin') {
      throw redirect({ to: '/admin' });
    }
    if (user.role === 'tutor' && user.isApprovedTutor) {
      throw redirect({ to: '/dashboard' });
    }
    return null;
  },
  loader: async ({ context }) => {
    await context.queryClient.prefetchQuery(myProgressQuery);
    return null;
  },
  component: ProgressPage,
});

function ProgressPage(): ReactNode {
  const { queryClient } = Route.useRouteContext();
  const session = queryClient.getQueryData(freshSessionQuery.queryKey) as
    { ok?: boolean; data?: { user?: { name: string } } } | undefined;
  const studentName = session?.ok ? (session.data?.user?.name ?? 'Siswa') : 'Siswa';

  const liveQuery = useQuery(myProgressQuery);
  const progress: ProgressSummary | null = liveQuery.data?.ok ? liveQuery.data.data : null;

  const aggregate = progress?.aggregate ?? {
    gamesStarted: 0,
    gamesCompleted: 0,
    totalStars: 0,
    worksheetsCompleted: 0,
    totalWorksheets: 0,
    streakDays: 0,
    badgesEarned: 0,
  };
  const games = progress?.games ?? [];
  const worksheets = progress?.worksheets ?? [];
  const recent = progress?.recentActivity ?? [];

  return (
    <main className="page-shell" data-testid="me-progress-page">
      <ProgressHeader studentName={studentName} aggregate={aggregate} />

      <section className="section" aria-labelledby="games-section">
        <h2 className="section-title" id="games-section">
          Progres game ({games.length})
        </h2>
        {games.length > 0 ? (
          <div className="card-grid">
            {games.map((entry) => (
              <GameProgressCard key={entry.gameId} entry={entry} />
            ))}
          </div>
        ) : (
          <p className="form-message" style={{ color: 'var(--color-ink-300)' }}>
            Belum ada game yang dimulai.
          </p>
        )}
      </section>

      <section className="section" aria-labelledby="worksheets-section">
        <h2 className="section-title" id="worksheets-section">
          Worksheet ({worksheets.length})
        </h2>
        {worksheets.length > 0 ? (
          <div className="card-grid">
            {worksheets.map((entry) => (
              <WorksheetProgressCard key={entry.worksheetId} entry={entry} />
            ))}
          </div>
        ) : (
          <p className="form-message" style={{ color: 'var(--color-ink-300)' }}>
            Worksheet belum tersedia.
          </p>
        )}
      </section>

      <section className="section" aria-labelledby="activity-section">
        <h2 className="section-title" id="activity-section">
          Aktivitas terbaru
        </h2>
        <RecentActivityFeed events={recent} limit={12} />
      </section>

      <section className="section cluster">
        <Link className="button button-ghost" to="/me">
          ← Kembali ke dasbor
        </Link>
        <Link className="button button-secondary" to="/me/badges">
          Lihat lencana
        </Link>
      </section>
    </main>
  );
}
